// MIT License - Copyright (c) 2026 Doug McNall
// API Service Layer for DD Hot Sauce Admin Tool
//
// Cookie-based JWT auth: tokens live in HttpOnly cookies (ddhs_admin_access /
// _refresh) that JS can't read. The non-HttpOnly ddhs_admin_csrf cookie is
// readable; we echo it in X-CSRF-Token on state-changing requests.
//
// Pattern adapted from
// codeSnippets/snippets/javascript/cookie-jwt-auth-with-refresh-rotation/.

import apiConfig from '../config/api-config';

const API_BASE_URL = apiConfig.apiUrl;

// -- CSRF: read the non-HttpOnly CSRF cookie ----------------------------
const _csrfToken = () => {
  const m = document.cookie.match(/(?:^|;\s*)ddhs_admin_csrf=([^;]+)/);
  return m ? m[1] : '';
};

// -- Refresh dedup: one in-flight refresh shared across concurrent 401s --
let _refreshInFlight = null;
const _tryRefresh = async () => {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': _csrfToken() },
        body: '{}',
      });
      return res.ok;
    } catch {
      return false;
    }
  })();
  try { return await _refreshInFlight; } finally { _refreshInFlight = null; }
};

const _redirectToLogin = () => {
  if (window.location.pathname !== '/login' && !window.location.pathname.endsWith('/reset-password')) {
    window.location.href = '/admin-tool/';
  }
};

const apiCall = async (endpoint, options = {}, _retried = false) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const method = (options.method || 'GET').toUpperCase();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  // CSRF header for state-changing methods (matches what the gateway middleware checks)
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = _csrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const config = {
    method: 'GET',
    ...options,
    credentials: 'include',  // send/receive cookies
    headers,
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  config.signal = controller.signal;

  let response;
  try {
    response = await fetch(url, config);
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    if (response.status === 401 && !_retried && !endpoint.startsWith('/auth/')) {
      // Try a refresh and retry the original call once
      if (await _tryRefresh()) {
        return apiCall(endpoint, options, true);
      }
      _redirectToLogin();
      throw new Error('Authentication required - please log in again');
    }
    if (response.status === 403) {
      // Could be CSRF or a plain authz failure — surface the detail
      const txt = await response.text();
      throw new Error(`HTTP 403: ${txt}`);
    }
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  return { success: true, data };
};

// Products API
export const productsApi = {
  getAll: async () => apiCall('/products'),
  getById: async (id) => apiCall(`/products/${id}`),
  getByCategory: async (category) => apiCall(`/products?category=${encodeURIComponent(category)}`),
  create: async (data) => apiCall('/products', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiCall(`/products/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiCall(`/products/${id}`, { method: 'DELETE' }),
};

// User Profile API
export const userProfileApi = {
  // GET /users/:id - Get user profile
  getProfile: async (userId) => {
    return await apiCall(`/users/${userId}`);
  },

  // PUT /users/:id - Update user profile
  updateProfile: async (userId, userData) => {
    return await apiCall(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }
};

// User Orders API
export const userOrdersApi = {
  // GET /orders?userId=:userId - Get user's orders
  getOrders: async (userId) => {
    return await apiCall(`/orders?userId=${userId}`);
  },

  // GET /orders/:id - Get specific order
  getOrderById: async (orderId) => {
    return await apiCall(`/orders/${orderId}`);
  }
};

// Orders API (Admin)
export const ordersApi = {
  // GET /orders
  getAll: async () => {
    return await apiCall('/orders');
  },

  // GET /orders/:id
  getById: async (orderId) => {
    return await apiCall(`/orders/${orderId}`);
  },

  // POST /orders
  create: async (orderData) => {
    return await apiCall('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  // PUT /orders/:id/status
  updateStatus: async (orderId, status) => {
    return await apiCall(`/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  // PUT /orders/:id/tracking
  updateTracking: async (orderId, trackingNumber, carrier = '') => {
    return await apiCall(`/orders/${orderId}/tracking`, {
      method: 'PUT',
      body: JSON.stringify({ trackingNumber, carrier }),
    });
  },

  // PUT /orders/:id/notes
  updateNotes: async (orderId, notes) => {
    return await apiCall(`/orders/${orderId}/notes`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  },
};

// Shopping Cart API (for future e-commerce features)
export const cartApi = {
  // GET /cart?userId=:userId
  get: async (userId) => {
    return await apiCall(`/cart?userId=${userId}`);
  },

  // POST /cart/add
  addItem: async (userId, productId, quantity) => {
    return await apiCall('/cart/add', {
      method: 'POST',
      body: JSON.stringify({ userId, productId, quantity }),
    });
  },

  // PUT /cart/update
  updateItem: async (userId, productId, quantity) => {
    return await apiCall('/cart/update', {
      method: 'PUT',
      body: JSON.stringify({ userId, productId, quantity }),
    });
  },

  // DELETE /cart/remove
  removeItem: async (userId, productId) => {
    return await apiCall('/cart/remove', {
      method: 'DELETE',
      body: JSON.stringify({ userId, productId }),
    });
  },

  // DELETE /cart/clear
  clear: async (userId) => {
    return await apiCall('/cart/clear', {
      method: 'DELETE',
      body: JSON.stringify({ userId }),
    });
  }
};

// Payment API (for future e-commerce features)
export const paymentApi = {
  // POST /payment/process
  processPayment: async (paymentData) => {
    return await apiCall('/payment/process', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },

  // POST /payment/validate-card
  validateCard: async (cardData) => {
    return await apiCall('/payment/validate-card', {
      method: 'POST',
      body: JSON.stringify(cardData),
    });
  }
};

// Newsletter API (for future marketing features)
export const newsletterApi = {
  // POST /newsletter/subscribe
  subscribe: async (email) => {
    return await apiCall('/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // POST /newsletter/unsubscribe
  unsubscribe: async (email) => {
    return await apiCall('/newsletter/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }
};

// Addresses API (Customer shipping addresses)
export const addressesApi = {
  // GET /addresses?userId=:userId
  getAll: async (userId) => {
    return await apiCall(`/addresses?userId=${userId}`);
  },

  // POST /addresses - Create new address
  create: async (addressData) => {
    return await apiCall('/addresses', {
      method: 'POST',
      body: JSON.stringify(addressData),
    });
  },

  // PUT /addresses/:id - Update address
  update: async (id, addressData) => {
    return await apiCall(`/addresses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(addressData),
    });
  },

  // DELETE /addresses/:id - Delete address
  delete: async (id) => {
    return await apiCall(`/addresses/${id}`, {
      method: 'DELETE',
    });
  },

  // GET /addresses/:id
  getById: async (id) => {
    return await apiCall(`/addresses/${id}`);
  }
};

// Saved Payment Methods API (for future e-commerce features)
export const savedPaymentsApi = {
  // GET /api/payment-methods
  getAll: async () => {
    return await apiCall('/payment-methods');
  },

  // GET /api/payment-methods/:id
  getById: async (id) => {
    return await apiCall(`/payment-methods/${id}`);
  }
};

// Categories API
export const categoriesApi = {
  // GET /categories
  getAll: async () => {
    return await apiCall('/categories');
  },

  // GET /categories/:id
  getById: async (id) => {
    return await apiCall(`/categories/${id}`);
  },

  // POST /categories
  create: async (data) => {
    return await apiCall('/categories', { method: 'POST', body: JSON.stringify(data) });
  },

  // PUT /categories/:id
  update: async (id, data) => {
    return await apiCall(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  // DELETE /categories/:id
  delete: async (id) => {
    return await apiCall(`/categories/${id}`, { method: 'DELETE' });
  }
};

// Reviews API
export const reviewsApi = {
  // GET /reviews
  getAll: async () => {
    return await apiCall('/reviews');
  },

  // GET /reviews?productId=:productId
  getByProductId: async (productId) => {
    return await apiCall(`/reviews?productId=${productId}`);
  },

  // POST /reviews
  create: async (reviewData) => {
    return await apiCall('/reviews', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  },

  // PUT /reviews/:id
  update: async (id, data) => {
    return await apiCall(`/reviews/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  // DELETE /reviews/:id
  delete: async (id) => {
    return await apiCall(`/reviews/${id}`, { method: 'DELETE' });
  }
};

// Recipes API
export const recipesApi = {
  // GET /recipes
  getAll: async () => {
    return await apiCall('/recipes');
  },

  // GET /recipes/:id
  getById: async (id) => {
    return await apiCall(`/recipes/${id}`);
  },

  // GET /recipes?category=:category
  getByCategory: async (category) => {
    return await apiCall(`/recipes?category=${encodeURIComponent(category)}`);
  },

  // POST /recipes
  create: async (data) => {
    return await apiCall('/recipes', { method: 'POST', body: JSON.stringify(data) });
  },

  // PUT /recipes/:id
  update: async (id, data) => {
    return await apiCall(`/recipes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  // DELETE /recipes/:id
  delete: async (id) => {
    return await apiCall(`/recipes/${id}`, { method: 'DELETE' });
  }
};

// Promotions API
export const promotionsApi = {
  getAll: async () => apiCall('/promotions'),
  create: async (data) => apiCall('/promotions', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiCall(`/promotions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiCall(`/promotions/${id}`, { method: 'DELETE' }),
};

// Customers API (Admin)
export const customersApi = {
  // GET /customers — supports optional filters: { status, approval }
  getAll: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.approval) qs.set('approval', params.approval);
    const query = qs.toString();
    return await apiCall(`/customers${query ? `?${query}` : ''}`);
  },

  // GET /customers/:id
  getById: async (id) => {
    return await apiCall(`/customers/${id}`);
  },

  // POST /customers/:id/approve — admin gates customer for purchases
  approve: async (id) => {
    return await apiCall(`/customers/${id}/approve`, { method: 'POST' });
  },

  // POST /customers/:id/unapprove — revoke purchase approval
  unapprove: async (id) => {
    return await apiCall(`/customers/${id}/unapprove`, { method: 'POST' });
  },

  // GET /customers/:id/addresses
  getAddresses: async (customerId) => {
    return await apiCall(`/customers/${customerId}/addresses`);
  },

  // POST /customers/:id/addresses
  createAddress: async (customerId, addressData) => {
    return await apiCall(`/customers/${customerId}/addresses`, {
      method: 'POST',
      body: JSON.stringify(addressData),
    });
  },

  // PUT /addresses/:id
  updateAddress: async (addressId, addressData) => {
    return await apiCall(`/addresses/${addressId}`, {
      method: 'PUT',
      body: JSON.stringify(addressData),
    });
  },

  // DELETE /addresses/:id
  deleteAddress: async (addressId) => {
    return await apiCall(`/addresses/${addressId}`, {
      method: 'DELETE',
    });
  }
};

// System Settings API (Admin) — name/value parameters table
export const systemSettingsApi = {
  // GET /system-settings — all settings
  getAll: async () => {
    return await apiCall('/system-settings');
  },

  // GET /system-settings/:key — one setting
  get: async (key) => {
    return await apiCall(`/system-settings/${key}`);
  },

  // PUT /system-settings/:key — update value (gateway coerces by value_type)
  update: async (key, value) => {
    return await apiCall(`/system-settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value: String(value) }),
    });
  },
};

// Admin Users API
export const adminUsersApi = {
  // GET /access-control/users
  getAll: async () => {
    return await apiCall('/access-control/users');
  },

  // POST /access-control/user
  create: async (userData) => {
    return await apiCall('/access-control/user', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  // DELETE /access-control/user/:email
  delete: async (email) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    });
  },

  // PUT /access-control/user/:email/password
  resetPassword: async (email, newPassword) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(email)}/password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword }),
    });
  },

  // PUT /access-control/user/:email/status
  updateStatus: async (email, enabled) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(email)}/status`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }
};

// Access Control API
export const accessControlApi = {
  // GET /access-control/user/:email - Get admin user profile
  getUserProfile: async (email) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(email)}`);
  },

  // POST /access-control/user - Create admin user
  createAdminUser: async (userData) => {
    return await apiCall('/access-control/user', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  // PUT /access-control/user/:email/group - Update user group
  updateUserGroup: async (userEmail, groupData) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/group`, {
      method: 'PUT',
      body: JSON.stringify(groupData),
    });
  },

  // PUT /access-control/user/:email/last-login - Update last login
  updateLastLogin: async (userEmail) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/last-login`, {
      method: 'PUT',
    });
  },

  // PUT /access-control/user/:email/profile - Update user profile
  updateUserProfile: async (userEmail, profileData) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/profile`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  // GET /access-control/functional-roles
  getAllFunctionalRoles: async () => {
    return await apiCall('/access-control/functional-roles');
  },

  // POST /access-control/functional-roles
  createFunctionalRole: async (data) => {
    return await apiCall('/access-control/functional-roles', { method: 'POST', body: JSON.stringify(data) });
  },

  // PUT /access-control/functional-roles/:id
  updateFunctionalRole: async (id, data) => {
    return await apiCall(`/access-control/functional-roles/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  // DELETE /access-control/functional-roles/:id
  deleteFunctionalRole: async (id) => {
    return await apiCall(`/access-control/functional-roles/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // PUT /access-control/user/:email/functional-role
  assignFunctionalRole: async (userEmail, roleId) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/functional-role`, { method: 'PUT', body: JSON.stringify({ role_id: roleId }) });
  },

  // GET /access-control/groups - Get all user groups
  getAllGroups: async () => {
    return await apiCall('/access-control/groups');
  },

  // GET /access-control/group/:groupId - Get specific group
  getGroup: async (groupId) => {
    return await apiCall(`/access-control/group/${groupId}`);
  },

  // POST /access-control/groups - Create new group
  createGroup: async (groupData) => {
    return await apiCall('/access-control/groups', {
      method: 'POST',
      body: JSON.stringify(groupData),
    });
  },

  // PUT /access-control/group/:groupId - Update group
  updateGroup: async (groupId, groupData) => {
    return await apiCall(`/access-control/group/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(groupData),
    });
  },

  // DELETE /access-control/group/:groupId - Delete group
  deleteGroup: async (groupId) => {
    return await apiCall(`/access-control/group/${groupId}`, {
      method: 'DELETE',
    });
  },

  // GET /access-control/permissions - Get all permissions
  getAllPermissions: async () => {
    return await apiCall('/access-control/permissions');
  },

  // GET /access-control/permissions/:groupId - Get permissions for group
  getPermissions: async (groupId) => {
    return await apiCall(`/access-control/permissions/${groupId}`);
  },

  // GET /access-control/users - Get all admin users (with permission filtering)
  getAllAdminUsers: async (requestingUserEmail) => {
    return await apiCall(`/access-control/users?requestingUser=${encodeURIComponent(requestingUserEmail)}`);
  },

  // GET /access-control/user/:email/tab-assignments - Get user's tab group assignments
  getUserTabGroupAssignments: async (userEmail) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/tab-assignments`);
  },

  // POST /access-control/user/:email/tab-assignments - Add user to tab group
  addUserToTabGroup: async (userEmail, tabId, role) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/tab-assignments`, {
      method: 'POST',
      body: JSON.stringify({ tab_group_id: tabId, role }),
    });
  },

  // DELETE /access-control/user/:email/tab-assignments/:tabId - Remove user from tab group
  removeUserFromTabGroup: async (userEmail, tabId) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/tab-assignments/${tabId}`, {
      method: 'DELETE',
    });
  },

  // PUT /access-control/user/:email/tab-assignments/:tabId - Update user's role in tab group
  updateUserRoleInTabGroup: async (userEmail, tabId, newRole) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/tab-assignments/${tabId}`, {
      method: 'PUT',
      body: JSON.stringify({ role: newRole }),
    });
  },

  // PUT /access-control/user/:email/password - Reset a user's password
  resetPassword: async (userEmail, newPassword) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword }),
    });
  },

  // PUT /access-control/user/:email/unlock - Unlock a locked admin account
  unlockAccount: async (userEmail) => {
    return await apiCall(`/access-control/user/${encodeURIComponent(userEmail)}/unlock`, {
      method: 'PUT',
    });
  }
};

// Payment Gateways API (Admin)
export const paymentGatewaysApi = {
  // GET /payment-gateways
  getAll: async () => {
    return await apiCall('/payment-gateways');
  },

  // GET /payment-gateways/:id
  getById: async (gatewayId) => {
    return await apiCall(`/payment-gateways/${gatewayId}`);
  },

  // POST /payment-gateways
  create: async (gatewayData) => {
    return await apiCall('/payment-gateways', {
      method: 'POST',
      body: JSON.stringify(gatewayData),
    });
  },

  // PUT /payment-gateways/:id
  update: async (gatewayId, gatewayData) => {
    return await apiCall(`/payment-gateways/${gatewayId}`, {
      method: 'PUT',
      body: JSON.stringify(gatewayData),
    });
  },

  // DELETE /payment-gateways/:id
  delete: async (gatewayId) => {
    return await apiCall(`/payment-gateways/${gatewayId}`, {
      method: 'DELETE',
    });
  },

  // POST /payment-gateways/:id/test
  test: async (gatewayId) => {
    return await apiCall(`/payment-gateways/${gatewayId}/test`, {
      method: 'POST',
    });
  },

  // POST /payment-gateways/:id/sync
  sync: async (gatewayId) => {
    return await apiCall(`/payment-gateways/${gatewayId}/sync`, {
      method: 'POST',
    });
  }
};

// Manufacturing API (Admin)
export const manufacturingApi = {
  // Manufacturing Batches
  batches: {
    getAll: () => apiCall('/manufacturing/batches'),
    getById: (id) => apiCall(`/manufacturing/batches/${id}`),
    create: (batchData) => apiCall('/manufacturing/batches', {
      method: 'POST',
      body: JSON.stringify(batchData)
    }),
    update: (id, batchData) => apiCall(`/manufacturing/batches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(batchData)
    }),
    delete: (id) => apiCall(`/manufacturing/batches/${id}`, {
      method: 'DELETE'
    })
  },
  
  // Material Pricing
  materials: {
    getAll: () => apiCall('/manufacturing/materials'),
    getById: (id) => apiCall(`/manufacturing/materials/${id}`),
    create: (materialData) => apiCall('/manufacturing/materials', {
      method: 'POST',
      body: JSON.stringify(materialData)
    }),
    update: (id, materialData) => apiCall(`/manufacturing/materials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(materialData)
    }),
    delete: (id) => apiCall(`/manufacturing/materials/${id}`, {
      method: 'DELETE'
    })
  }
};

// Revenue API (Admin)
export const revenueApi = {
  // Revenue Overview
  getOverview: () => apiCall('/revenue/overview'),
  
  // Revenue by Products
  getProductBreakdown: () => apiCall('/revenue/products'),
  
  // Monthly Revenue Data
  getMonthlyData: () => apiCall('/revenue/monthly')
};

// Shipping API (Admin)
export const shippingApi = {
  // Shipping Carriers
  carriers: {
    // GET /shipping-carriers
    getAll: async () => {
      return await apiCall('/shipping-carriers');
    },

    // GET /shipping-carriers/:id
    getById: async (carrierId) => {
      return await apiCall(`/shipping-carriers/${carrierId}`);
    },

    // POST /shipping-carriers
    create: async (carrierData) => {
      return await apiCall('/shipping-carriers', {
        method: 'POST',
        body: JSON.stringify(carrierData),
      });
    },

    // PUT /shipping-carriers/:id
    update: async (carrierId, carrierData) => {
      return await apiCall(`/shipping-carriers/${carrierId}`, {
        method: 'PUT',
        body: JSON.stringify(carrierData),
      });
    },

    // DELETE /shipping-carriers/:id
    delete: async (carrierId) => {
      return await apiCall(`/shipping-carriers/${carrierId}`, {
        method: 'DELETE',
      });
    }
  },

  // Shipping Zones
  zones: {
    // GET /shipping-zones
    getAll: async () => {
      return await apiCall('/shipping-zones');
    },

    // GET /shipping-zones/:id
    getById: async (zoneId) => {
      return await apiCall(`/shipping-zones/${zoneId}`);
    },

    // POST /shipping-zones
    create: async (zoneData) => {
      return await apiCall('/shipping-zones', {
        method: 'POST',
        body: JSON.stringify(zoneData),
      });
    },

    // PUT /shipping-zones/:id
    update: async (zoneId, zoneData) => {
      return await apiCall(`/shipping-zones/${zoneId}`, {
        method: 'PUT',
        body: JSON.stringify(zoneData),
      });
    },

    // DELETE /shipping-zones/:id
    delete: async (zoneId) => {
      return await apiCall(`/shipping-zones/${zoneId}`, {
        method: 'DELETE',
      });
    }
  },

  // Shipping Rates
  rates: {
    // GET /shipping-rates
    getAll: async () => {
      return await apiCall('/shipping-rates');
    }
  }
};

// Audit Logs API (Supervisor only)
const auditLogsApi = {
  // GET /audit-logs
  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.log_type) queryParams.append('log_type', filters.log_type);
    if (filters.user_email) queryParams.append('user_email', filters.user_email);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.limit) queryParams.append('limit', filters.limit);
    
    const endpoint = queryParams.toString() 
      ? `/audit-logs?${queryParams.toString()}`
      : '/audit-logs';
    
    return await apiCall(endpoint);
  },

  // GET /audit-logs/stats (if implemented)
  getStats: async () => {
    return await apiCall('/audit-logs/stats');
  }
};

// Default export with all APIs
export const recallApi = {
  lookup: async (lotNumber) => apiCall(`/recall/lookup?lot_number=${encodeURIComponent(lotNumber)}`),
};

// Support Tickets API
export const supportTicketsApi = {
  getAll: (status) => apiCall(`/support-tickets${status && status !== 'all' ? `?status=${status}` : ''}`),
  getById: (id) => apiCall(`/support-tickets/${id}`),
  updateStatus: (id, status) => apiCall(`/support-tickets/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  assign: (id, assignedTo) => apiCall(`/support-tickets/${id}/assign`, { method: 'PUT', body: JSON.stringify({ assigned_to: assignedTo }) }),
  updateTags: (id, tags) => apiCall(`/support-tickets/${id}/tags`, { method: 'PUT', body: JSON.stringify({ tags }) }),
  getMessages: (id) => apiCall(`/support-tickets/${id}/messages`),
  sendMessage: (id, message) => apiCall(`/support-tickets/${id}/messages`, { method: 'POST', body: JSON.stringify(message) }),
  getAgents: () => apiCall('/support/agents'),
  sendCsat: (id) => apiCall(`/support/tickets/${id}/csat`, { method: 'POST' }),
  checkSla: () => apiCall('/support/sla-check', { method: 'POST' }),
  uploadAttachment: (id, formData) => {
    // FormData uploads bypass the JSON apiCall but still need cookies + CSRF.
    const csrf = _csrfToken();
    return fetch(`${API_BASE_URL}/support/tickets/${id}/attachments`, {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'X-CSRF-Token': csrf } : {},
      body: formData,
    }).then(r => r.json()).then(data => ({ data }));
  },
};

export const mediaApi = {
  getUploadUrl: (filename, content_type, folder) =>
    apiCall('/media/upload-url', { method: 'POST', body: JSON.stringify({ filename, content_type, folder }) }),
  uploadToS3: async (uploadUrl, file) => {
    const res = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    if (!res.ok) throw new Error('S3 upload failed');
  },
  graphics: {
    getAll: (category) => apiCall(`/media/graphics${category ? `?category=${category}` : ''}`),
    create: (data) => apiCall('/media/graphics', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiCall(`/media/graphics/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiCall(`/media/graphics/${id}`, { method: 'DELETE' }),
  },
};

const api = {
  coupons: {
    getAll: () => apiCall('/coupons'),
    create: (data) => apiCall('/coupons', { method: 'POST', body: JSON.stringify(data) }),
    update: (code, data) => apiCall(`/coupons/${code}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (code) => apiCall(`/coupons/${code}`, { method: 'DELETE' })
  },
  products: productsApi,
  userProfile: userProfileApi,
  userOrders: userOrdersApi,
  orders: ordersApi,
  cart: cartApi,
  payment: paymentApi,
  newsletter: newsletterApi,
  addresses: addressesApi,
  savedPayments: savedPaymentsApi,
  categories: categoriesApi,
  reviews: reviewsApi,
  recipes: recipesApi,
  promotions: promotionsApi,
  customers: customersApi,
  adminUsers: adminUsersApi,
  accessControl: accessControlApi,
  paymentGateways: paymentGatewaysApi,
  shipping: shippingApi,
  manufacturing: manufacturingApi,
  revenue: revenueApi,
  auditLogs: auditLogsApi,
  recall: recallApi,
  supportTickets: supportTicketsApi,
  media: mediaApi,
  systemSettings: systemSettingsApi,
};

export default api;
